import { isPlatformServer, registerLocaleData } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import localePl from '@angular/common/locales/pl';
import {
  APP_ID,
  ApplicationConfig,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  PLATFORM_ID,
  provideAppInitializer,
} from '@angular/core';
import {
  HammerModule,
  Meta,
  provideClientHydration,
  withEventReplay,
  withHttpTransferCacheOptions,
  withIncrementalHydration,
} from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { withNgxsFormPlugin } from '@ngxs/form-plugin';
import { withNgxsLoggerPlugin } from '@ngxs/logger-plugin';
import { withNgxsRouterPlugin } from '@ngxs/router-plugin';
import { StorageOption, withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { provideStore, Store } from '@ngxs/store';
import {
  clearJwtHpCookie,
  GOOGLE_TAG_ID,
  HttpErrorInterceptor,
  IS_DEVELOPMENT,
  ONE_SCRIPT_DATA_DOMAIN,
  PLATFORM,
  Platform,
  SERVICES_API,
  UserService,
  WwwAppStateSetUserData,
} from '@pro-connect/shared';
import { APP_ROUTES } from '@www/app.routing';
import { RECAPTCHA_LOADER_OPTIONS, RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { Angulartics2Module } from 'angulartics2';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { provideNgxMask } from 'ngx-mask';
import { QuicklinkModule } from 'ngx-quicklink';
import { provideToastr } from 'ngx-toastr';
import { EMPTY, firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';
import { APP_ACCOUNT_ORDERS, APP_COOKIES_KEY, APP_JWT_TOKEN, APP_TOKEN_EXPIRED_AT } from './app.consts';
import { AppState } from './app.state';

registerLocaleData(localePl);

const LOCAL_STORAGE_STATE_VALUES = [APP_JWT_TOKEN, APP_TOKEN_EXPIRED_AT, APP_COOKIES_KEY, APP_ACCOUNT_ORDERS];

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: APP_ID, useValue: 'serverApp' },
    provideRouter(
      APP_ROUTES,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      }),
    ),
    provideAnimations(),
    provideToastr({
      timeOut: 10000,
      positionClass: 'toast-top-right',
      preventDuplicates: true,
    }),
    provideNgxMask({
      decimalMarker: ',',
    }),
    provideClientHydration(
      withIncrementalHydration(),
      withEventReplay(),
      withHttpTransferCacheOptions({
        includePostRequests: true,
      }),
    ),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideStore([AppState], {
      developmentMode: !environment.production,
      selectorOptions: {
        suppressErrors: false,
      },
      compatibility: {
        strictContentSecurityPolicy: true,
      },
    }),
    withNgxsLoggerPlugin({
      logger: console,
      collapsed: false,
      disabled: environment.production,
    }),
    withNgxsRouterPlugin(),
    withNgxsFormPlugin(),
    withNgxsStoragePlugin({
      storage: StorageOption.LocalStorage,
      keys: [...LOCAL_STORAGE_STATE_VALUES],
    }),

    importProvidersFrom(BsDropdownModule.forRoot()),
    importProvidersFrom(QuicklinkModule),
    importProvidersFrom(NgSelectModule),
    importProvidersFrom(HammerModule),
    importProvidersFrom(RecaptchaV3Module),
    importProvidersFrom(BsDatepickerModule.forRoot()),
    importProvidersFrom(Angulartics2Module.forRoot()),
    { provide: APP_ID, useValue: 'serverApp' },
    { provide: LOCALE_ID, useValue: 'pl-PL' },
    {
      provide: RECAPTCHA_LOADER_OPTIONS,
      useFactory: (locale: string) => ({
        onBeforeLoad(url: URL) {
          url.searchParams.set('hl', locale);
          return { url };
        },
      }),
      deps: [LOCALE_ID],
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.recaptchaV3SiteKey,
    },
    // {
    //   provide: HTTP_INTERCEPTORS,
    //   useClass: HttpErrorInterceptor,
    //   multi: true,
    // },
    { provide: SERVICES_API, useValue: environment.api },
    { provide: PLATFORM, useValue: Platform.PRO_CONNECT },
    { provide: ONE_SCRIPT_DATA_DOMAIN, useValue: environment.oneScriptDataDomainKey },
    { provide: IS_DEVELOPMENT, useValue: environment.development },
    { provide: GOOGLE_TAG_ID, useValue: environment.gtm },
    provideAppInitializer(() => {
      const initializerFn = ((userService: UserService, store: Store, platformId: object, meta: Meta) => async () => {
        meta.removeTag('name="robots"');
        meta.addTag({
          name: 'robots',
          content: environment.production ? 'index, follow' : 'noindex, nofollow',
        });

        if (isPlatformServer(platformId)) {
          return EMPTY;
        }

        const jwtHp = document.cookie
          ?.split(';')
          ?.find((item) => item.includes('jwt_hp'))
          ?.split('=')[1];
        if (!jwtHp) {
          return EMPTY;
        }

        return await firstValueFrom(userService.getMe())
          .then((user) => {
            store.dispatch([new WwwAppStateSetUserData({ user })]);
          })
          .catch((reason) => {
            console.warn('Error while getting user data', reason);
            const status = reason?.status;
            if (status === 403 || status === 401) {
              clearJwtHpCookie();
            }
          });
      })(inject(UserService), inject(Store), inject(PLATFORM_ID), inject(Meta));
      return initializerFn();
    }),
  ],
};
